import React, { useMemo, useState } from "react";
import "./LearningLibrary.css";
import { Link } from "react-router-dom";
import { useGetPublicLearningResourcesQuery } from "../../services/api";

const TABS = ["Articles", "Videos", "Exercises", "Courses", "Workshops", "Other"];

const COMPETENCIES = [
  "All",
  "Communication",
  "Execution",
  "Leadership",
  "Strategy",
  "Collaboration",
  "Clarity",
];

const DURATIONS = ["Any", "≤ 15 min", "16–30 min", "31–60 min", "60+ min"];

export default function LearningLibrary() {
  const [tab, setTab] = useState("Articles");
  const [q, setQ] = useState("");
  const [comp, setComp] = useState("All");
  const [dur, setDur] = useState("Any");

  // Fetch resources from API
  const { data: resources = [], isLoading, error } = useGetPublicLearningResourcesQuery({
    type: tab,
    search: q.trim() || undefined,
  });

  const items = useMemo(() => {
    let rows = [...resources];

    // filter by competency (if the data has competency field)
    if (comp !== "All") {
      rows = rows.filter((i) => i.competency === comp);
    }

    // filter by duration (if the data has duration field)
    rows = rows.filter((i) => {
      if (dur === "Any") return true;
      if (!i.duration) return true; // If no duration, show by default
      if (dur === "≤ 15 min") return i.duration <= 15;
      if (dur === "16–30 min") return i.duration >= 16 && i.duration <= 30;
      if (dur === "31–60 min") return i.duration >= 31 && i.duration <= 60;
      if (dur === "60+ min") return i.duration > 60;
      return true;
    });

    return rows;
  }, [resources, comp, dur]);

  return (
    <div className="ll-container">
      <button className="welcome">← Back Home</button>

      <h1 className="username">Learning Library</h1>

      <div className="ll-card">
        <div className="ll-card-head">Library</div>

        {/* Tabs */}
        <div className="ll-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              className={`ll-tab ${tab === t ? "active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="ll-filters">
          <div className="ll-search">
            <svg
              className="ll-search-icon-left"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              className="ll-search-input"
              placeholder="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <svg
              className="ll-search-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>

          <button className="ll-filter-btn">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>

          <div className="ll-select-wrap">
            <select
              className="ll-select"
              value={comp}
              onChange={(e) => setComp(e.target.value)}
            >
              {COMPETENCIES.map((c) => (
                <option key={c} value={c}>
                  {c === "All" ? "Competency" : c}
                </option>
              ))}
            </select>
            <span className="ll-caret"></span>
          </div>

          <div className="ll-select-wrap">
            <select
              className="ll-select"
              value={dur}
              onChange={(e) => setDur(e.target.value)}
            >
              {DURATIONS.map((d) => (
                <option key={d} value={d}>
                  {d === "Any" ? "Duration" : d}
                </option>
              ))}
            </select>
            <span className="ll-caret"></span>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="ll-loading">Loading resources...</div>
        )}

        {/* Error State */}
        {error && (
          <div className="ll-error">
            Failed to load resources. Please try again later.
          </div>
        )}

        {/* List */}
        {!isLoading && !error && (
          <div className="ll-list">
            {items.map((it) => (
              <div key={it._id} className="ll-row">
                <div className="ll-left">
                  <div className="ll-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_1_6132)">
                        <path
                          d="M16 6H19C19.2652 6 19.5196 6.10536 19.7071 6.29289C19.8946 6.48043 20 6.73478 20 7V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20M18 20C17.4696 20 16.9609 19.7893 16.5858 19.4142C16.2107 19.0391 16 18.5304 16 18V5C16 4.73478 15.8946 4.48043 15.7071 4.29289C15.5196 4.10536 15.2652 4 15 4H5C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V17C4 17.7956 4.31607 18.5587 4.87868 19.1213C5.44129 19.6839 6.20435 20 7 20H18Z"
                          stroke="#51CC77"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 8H12"
                          stroke="#51CC77"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 12H12"
                          stroke="#51CC77"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 16H12"
                          stroke="#51CC77"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1_6132">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <div className="ll-text">
                    <div className="ll-item-title">{it.title}</div>
                    <div className="ll-item-blurb">{it.description}</div>
                  </div>
                </div>

                <div className="ll-right">
                  <Link className="ll-btn ghost" to={`/learning-library/${it._id}`}>
                    Details →
                  </Link>
                  <button className="ll-btn primary">Add To Plan →</button>
                </div>
              </div>
            ))}

            {items.length === 0 && (
              <div className="ll-empty">No resources found.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
